import Container from "@/components/ui/Container";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/utils";

interface CtaLink {
  label: string;
  href: string;
  external?: boolean;
}

interface CtaBandProps {
  eyebrow?: string;
  heading: string;
  subheading?: string;
  primaryCta: CtaLink;
  secondaryCta?: CtaLink;
  /** "purple" (default) | "green" */
  accent?: "purple" | "green";
  className?: string;
}

export default function CtaBand({
  eyebrow,
  heading,
  subheading,
  primaryCta,
  secondaryCta,
  accent = "purple",
  className,
}: CtaBandProps) {
  return (
    <section className={cn("section-pad bg-bg-surface", className)}>
      <Container>
        <div
          className={cn(
            "gradient-border rounded-2xl p-8 text-center sm:p-12",
            accent === "green" ? "bg-glow-green" : "bg-glow-center",
          )}
        >
          {eyebrow && (
            <p
              className={cn(
                "mb-3 text-xs font-semibold uppercase tracking-widest",
                accent === "green" ? "text-brand-green" : "text-brand-purple-light",
              )}
            >
              {eyebrow}
            </p>
          )}

          <h2 className="mb-4 text-3xl font-bold text-white sm:text-4xl">{heading}</h2>

          {subheading && (
            <p className="mx-auto mb-8 max-w-lg text-base leading-relaxed text-text-secondary">
              {subheading}
            </p>
          )}

          {/* Mobile: full-width stacked CTAs; ≥sm: centered inline row (unchanged). */}
          <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            <Button
              href={primaryCta.href}
              variant={accent === "green" ? "green" : "primary"}
              size="lg"
              className="w-full sm:w-auto"
              {...(primaryCta.external && { target: "_blank", rel: "noopener noreferrer" })}
            >
              {primaryCta.label}
            </Button>

            {secondaryCta && (
              <Button
                href={secondaryCta.href}
                variant="outline"
                size="lg"
                className="w-full sm:w-auto"
                {...(secondaryCta.external && { target: "_blank", rel: "noopener noreferrer" })}
              >
                {secondaryCta.label}
              </Button>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
}
